import React, { useState, useEffect, useRef } from 'react';
import { Trash2, FileText } from 'lucide-react';
import RichTextEditor, { RichTextEditorRef } from '../../../../atoms/RichTextEditor';
import DeleteJobModal from '../DeleteJobModal';
import api from '../../../../../utils/api';
import { API_ENDPOINTS } from '../../../../../config';

interface NoteFromAPI {
  uid: string;
  job_uid: string;
  title: string;
  short_text: string;
  note: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface Note {
  uid: string;
  content: string;
  createdAt: string;
}

interface NotesTabProps {
  jobId?: string;
  focusEditor?: boolean;
  onEditorFocused?: () => void;
  highlightEntityUid?: string | null;
}

const formatTimeAgo = (dateString: string | undefined): string => {
  if (!dateString) return 'Unknown';
  
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) {
      return 'Just now';
    }
    
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} ${diffInMinutes === 1 ? 'minute' : 'minutes'} ago`;
    }
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ago`;
    }
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {
      return `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} ago`;
    }
    
    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) {
      return `${diffInWeeks} ${diffInWeeks === 1 ? 'week' : 'weeks'} ago`;
    }
    
    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
      return `${diffInMonths} ${diffInMonths === 1 ? 'month' : 'months'} ago`;
    }
    
    const diffInYears = Math.floor(diffInDays / 365);
    return `${diffInYears} ${diffInYears === 1 ? 'year' : 'years'} ago`;
  } catch (error) {
    return 'Unknown';
  }
};

export default function NotesTab({ jobId, focusEditor, onEditorFocused, highlightEntityUid }: NotesTabProps) {
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editNoteContent, setEditNoteContent] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<string | null>(null);
  const editorRef = useRef<RichTextEditorRef>(null);
  const editCardRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!highlightEntityUid || notes.length === 0) return;
    const el = document.querySelector(`[data-uid="${highlightEntityUid}"]`) as HTMLElement | null;
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ejm-entity-highlight');
      const t = setTimeout(() => el.classList.remove('ejm-entity-highlight'), 3500);
      return () => clearTimeout(t);
    }
  }, [highlightEntityUid, notes]);

  useEffect(() => {
    const fetchNotes = async () => {
      if (!jobId) {
        setNotes([]);
        return;
      }

      try {
        setLoading(true);
        const data = await api.get<NoteFromAPI[]>(API_ENDPOINTS.jobNotes.list(jobId));
        
        const transformedNotes: Note[] = data.map((apiNote) => ({
          uid: apiNote.uid,
          content: apiNote.note || '',
          createdAt: formatTimeAgo(apiNote.created_at),
        }));
        
        setNotes(transformedNotes);
      } catch (error) {

        setNotes([]);
      } finally {
        setLoading(false);
      }
    };

    fetchNotes();
  }, [jobId]);

  useEffect(() => {
    if (focusEditor && editorRef.current) {
      const timeoutId = setTimeout(() => {
        if (editorRef.current) {
          editorRef.current.focus();
          if (onEditorFocused) {
            onEditorFocused();
          }
        }
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [focusEditor, onEditorFocused]);

  const handleNewNoteChange = (html: string) => {
    setNewNoteContent(html);
  };

  const handleEditNoteChange = (html: string) => {
    setEditNoteContent(html);
  };

  const handleCreateNote = async () => {
    if (!newNoteContent.trim() || !jobId) return;

    try {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = newNoteContent;
      const plainText = tempDiv.textContent || tempDiv.innerText || '';
      const title = plainText.trim().substring(0, 50) || 'Note';

      const payload = {
        job_uid: jobId,
        title: title,
        note: newNoteContent.trim(),
      };

      const createdNote = await api.post<NoteFromAPI>(API_ENDPOINTS.jobNotes.create, payload);

      const transformedNote: Note = {
        uid: createdNote.uid,
        content: createdNote.note || '',
        createdAt: formatTimeAgo(createdNote.created_at),
      };

      setNotes([transformedNote, ...notes]);
      setNewNoteContent('');
    } catch (error) {

    }
  };

  const handleDeleteClick = (uid: string) => {
    setNoteToDelete(uid);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!noteToDelete) {
      setDeleteModalOpen(false);
      setNoteToDelete(null);
      return;
    }

    try {
      const note = notes.find(n => n.uid === noteToDelete);
      if (!note) {

        setDeleteModalOpen(false);
        setNoteToDelete(null);
        return;
      }

      await api.delete(API_ENDPOINTS.jobNotes.delete(note.uid));
      
      setNotes(notes.filter(note => note.uid !== noteToDelete));
    } catch (error: any) {

      if (error?.response?.status === 404) {
        setNotes(notes.filter(note => note.uid !== noteToDelete));
      }
    } finally {
      setDeleteModalOpen(false);
      setNoteToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setNoteToDelete(null);
  };

  const handleEditNote = (uid: string) => {
    const note = notes.find(n => n.uid === uid);
    if (note) {
      setEditingNoteId(note.uid);
      setEditNoteContent(note.content);
    } else {

    }
  };


  const handleSaveEdit = async () => {
    const currentEditingNoteId = editingNoteId;
    
    if (!currentEditingNoteId) {

      return;
    }

    const content = editNoteContent.trim();
    if (!content) {

      return;
    }

    try {
      const note = notes.find(n => n.uid === currentEditingNoteId);
      if (!note) {

        return;
      }

      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;
      const plainText = tempDiv.textContent || tempDiv.innerText || '';
      const title = plainText.trim().substring(0, 50) || 'Note';

      const payload = {
        uid: note.uid,
        title: title,
        note: content,
      };

      const updatedNote = await api.put<NoteFromAPI>(API_ENDPOINTS.jobNotes.update, payload);

      const originalNote = notes.find(n => n.uid === currentEditingNoteId);
      const createdAt = updatedNote.created_at 
        ? formatTimeAgo(updatedNote.created_at) 
        : (originalNote?.createdAt || 'Unknown');

      const transformedNote: Note = {
        uid: note.uid,
        content: content,
        createdAt: createdAt,
      };

      setNotes(notes.map(n => 
        n.uid === currentEditingNoteId 
          ? transformedNote
          : n
      ));
      
      setEditingNoteId(null);
      setEditNoteContent('');
    } catch (error) {

    }
  };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditNoteContent('');
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!editingNoteId || !editCardRef.current) return;
      
      const target = event.target as Node;
      
      if (editCardRef.current.contains(target)) {
        return;
      }
      
      const button = (target as Element).closest('button');
      if (button && editCardRef.current.contains(button)) {
        return;
      }
      
      handleCancelEdit();
    };

    if (editingNoteId) {
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [editingNoteId]);

  return (
    <div className="notes-tab-wrapper">
      <div className="notes-editor-wrapper">
        <RichTextEditor
          ref={editorRef}
          value={newNoteContent}
          onChange={handleNewNoteChange}
          placeholder="Take a note..."
          height={200}
        />
        <div className="notes-editor-footer">
          <button
            className="notes-save-btn"
            onClick={handleCreateNote}
            disabled={!newNoteContent.trim()}
            type="button"
          >
            Save
          </button>
        </div>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280', fontSize: '14px' }}>
          Loading notes...
        </div>
      )}

      {!loading && notes.length === 0 && (
        <div className="notes-empty">
          <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'center' }}>
            <FileText size={32} style={{ color: '#9ca3af' }} />
          </div>
          <div style={{ fontWeight: 600, marginBottom: '6px' }}>No notes yet</div>
          <div>Create your first note above</div>
        </div>
      )}

      {!loading && notes.length > 0 && (
        <div className="notes-grid">
          {notes.map((note) => (
            editingNoteId === note.uid ? (
              <div key={note.uid} className="note-card-editing" ref={editCardRef}>
                <div className="notes-editor-wrapper">
                  <RichTextEditor
                    value={editNoteContent}
                    onChange={handleEditNoteChange}
                    placeholder="Edit your note..."
                    height={200}
                  />
                  <div className="notes-editor-footer">
                    <button
                      className="notes-save-btn"
                      onClick={handleSaveEdit}
                      disabled={!editNoteContent.trim()}
                      type="button"
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div key={note.uid} className="note-card" data-uid={note.uid}>
                <div className="note-card-header">
                  <span className="note-card-time">{note.createdAt}</span>
                  <div className="note-card-actions">
                    <button
                      className="note-action-btn"
                      onClick={() => handleEditNote(note.uid)}
                      type="button"
                      title="Edit"
                    >
                      ✎
                    </button>
                    <button
                      className="note-action-btn note-action-btn--delete"
                      onClick={() => handleDeleteClick(note.uid)}
                      type="button"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <div 
                  className="note-card-content"
                  dangerouslySetInnerHTML={{ __html: note.content }}
                />
              </div>
            )
          ))}
        </div>
      )}

      <DeleteJobModal
        open={deleteModalOpen}
        title="Delete Note"
        message="Are you sure you want to delete this note? This action cannot be undone."
        confirmText="Delete Note"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
}
